<script lang="ts">
  import InspectorField from '../InspectorField.svelte';
  import ConfirmDialog from '../../ui/ConfirmDialog.svelte';
  import { updateScene, deleteScene } from '$lib/api';
  import { clearEntity } from '$lib/router';
  import { scenesQuery } from '$lib/queries/scenes';
  import { unitSceneLoadsQuery } from '$lib/queries/scene_loads';
  import { roomsQuery } from '$lib/queries/rooms';
  import { zonesQuery } from '$lib/queries/zones';
  import { unitsQuery } from '$lib/queries/units';
  import { projectsQuery } from '$lib/queries/projects';
  import { invalidateAfterSceneCreate } from '$lib/state/invalidation';
  import { router } from '$lib/router';

  let { id }: { id: number } = $props();

  const scenes = scenesQuery();
  const sceneLoads = unitSceneLoadsQuery();
  const rooms = roomsQuery();
  const zones = zonesQuery();
  const units = unitsQuery();
  const projects = projectsQuery();

  let unitId = $derived(router.unitId ?? 0);
  let scene = $derived((scenes.data ?? []).find((s) => s.id === id));
  let controlledLoadCount = $derived(
    (sceneLoads.data ?? []).filter((sl) => sl.scene === id).length
  );

  let unit = $derived((units.data ?? []).find((u) => u.id === unitId) ?? null);
  let project = $derived(
    (projects.data ?? []).find((p) => p.id === router.projectId) ?? null
  );

  let scopeLabel = $derived(
    (() => {
      if (!scene) return '—';
      const scopeId = scene.scope_id;
      switch (scene.scope) {
        case 'room': {
          const room = (rooms.data ?? []).find((r) => r.id === scopeId);
          return room ? `Room · ${room.name}` : 'Room';
        }
        case 'zone': {
          const zone = (zones.data ?? []).find((z) => z.id === scopeId);
          return zone ? `Zone · ${zone.name}` : 'Zone';
        }
        case 'unit':
          return unit ? `Unit · ${unit.name}` : 'Unit';
        case 'project':
          return project ? `Project · ${project.name}` : 'Project';
        default:
          return scene.scope || '—';
      }
    })()
  );

  let showDeleteConfirm = $state(false);

  async function saveName(name: string) {
    if (!scene) return;
    await updateScene(id, {
      name,
      unit: scene.unit,
      scope: scene.scope,
      scope_id: scene.scope_id,
      priority: scene.priority ?? 0
    });
    invalidateAfterSceneCreate(unitId);
  }

  async function handleDelete() {
    showDeleteConfirm = false;
    if (!scene) return;
    await deleteScene(scene.id);
    invalidateAfterSceneCreate(unitId);
    clearEntity();
  }
</script>

{#if scene}
  <div class="px-3 py-3 space-y-4">
    <div class="space-y-2">
      <InspectorField label="Name" value={scene.name} onSave={saveName} />

      <div class="space-y-1">
        <p class="text-2xs font-medium text-text-muted uppercase tracking-wide">Scope</p>
        <p class="text-sm text-text-primary">{scopeLabel}</p>
      </div>

      <div class="space-y-1">
        <p class="text-2xs font-medium text-text-muted uppercase tracking-wide">Controlled loads</p>
        <p class="text-sm font-mono text-text-primary">{controlledLoadCount}</p>
      </div>
    </div>

    <div class="pt-4 border-t border-border-default space-y-2">
      <p class="text-2xs font-medium text-text-muted uppercase tracking-wide">Danger zone</p>
      <button
        type="button"
        class="w-full px-2 py-1.5 text-sm text-state-error border border-state-error/30 rounded hover:bg-state-error/5 transition-colors duration-100"
        onclick={() => (showDeleteConfirm = true)}
      >
        Delete scene
      </button>
    </div>
  </div>
{:else}
  <div class="px-3 py-3 text-sm text-text-muted">Scene not found</div>
{/if}

<ConfirmDialog
  bind:open={showDeleteConfirm}
  title="Delete scene"
  message="Delete “{scene?.name}”? All scene load values will be removed."
  confirmText="Delete"
  onconfirm={handleDelete}
  oncancel={() => (showDeleteConfirm = false)}
/>
